/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";
import { ButtonBase } from "../ButtonBase";
import { Icon } from "../Icon";
import { ISelectOption_Item, isSelectOptionItem, TSelectOption } from "../Select";
import { Menu } from "../Menu";
import { MenuItem } from "../MenuItem";
import { ListItemIcon } from "../ListItemIcon";
import { ListItemText } from "../ListItemText";
import { Separator } from "../Separator";
import { useForkRef } from "../../../hooks";
import { isFunction, isNil } from "@adapptio/lib-shared";

import "./ModeButton.scss";

export interface IModeButtonProps {
	children?: React.ReactNode;
	className?: string;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	selected?: boolean;
	disabled?: boolean;
	options: TSelectOption[];
	value?: string;
	onChange?: (value: string) => void;
	preventCompactMode?: boolean;
}

export const ModeButton = React.forwardRef<HTMLButtonElement, IModeButtonProps>(
	(
		{ children, className, disabled, selected, onClick, options, value, onChange, preventCompactMode },
		ref
	) => {
		const elRef = useRef<HTMLButtonElement>();
		const forkRef = useForkRef(elRef, ref);

		const [ menuOpen, toggleMenu, openMenu, closeMenu ] = useToggle(false);

		const firstOption = isSelectOptionItem(options[0])
			? options[0]
			: isSelectOptionItem(options[1])
			? options[1]
			: null;
		const compactMode = !preventCompactMode && options?.length < 3;
		const compactModeActive = compactMode && value !== firstOption?.value;

		const baseClass = BEM("edt-mode-button", null, {
			selected: menuOpen || compactModeActive
		});

		if (className) {
			baseClass.add(className);
		}

		const selectedOption = useMemo<ISelectOption_Item>(
			() =>
				options?.find(
					(option) => isSelectOptionItem(option) && option?.value === value
				) as ISelectOption_Item,
			[ value, options ]
		);

		const switchSelectedOptions = useCallback<React.MouseEventHandler>(
			(event) => {
				const newOption = options.find(
					(option) => isSelectOptionItem(option) && option.value !== value
				);
				if (!isSelectOptionItem(newOption)) {
					return;
				}

				const newValue = newOption?.value;

				if (isFunction(newOption?.onClick)) {
					newOption.onClick(event);
				}

				if (!isNil(newValue) && isFunction(onChange)) {
					onChange(newValue);
				}
			},
			[ value, options ]
		);

		const handleClick = useCallback<IModeButtonProps["onClick"]>(
			(event) => {
				event.stopPropagation();

				if (compactMode) {
					switchSelectedOptions(event);
				} else {
					toggleMenu();
				}
			},
			[ compactMode, switchSelectedOptions, toggleMenu ]
		);

		const getOptionClickHandler = useCallback(
			(value: string) => () => {
				if (isFunction(onChange)) {
					onChange(value);
				}
			},
			[ onChange ]
		);

		return (
			<ButtonBase
				className={baseClass.toClassName()}
				onClick={handleClick}
				disabled={disabled}
				ref={forkRef}
			>
				<Icon
					name={compactMode ? "mdi/function-variant" : selectedOption?.icon}
					className={baseClass.element("icon").toClassName()}
				/>
				{!compactMode && (
					<>
						<Icon name="mdi/menu-down" className={baseClass.element("icon").toClassName()} />
						<Menu anchorRef={elRef} open={menuOpen} onClose={closeMenu}>
							{options?.map((option, index) =>
								isSelectOptionItem(option) ? (
									<MenuItem
										key={`option-${option.id || option.value}`}
										value={option.value}
										onClick={option.onClick || getOptionClickHandler(option.value)}
										selected={value === option.value}
									>
										{option.icon && (
											<ListItemIcon
												className={baseClass.element("option-icon").toClassName()}
											>
												<Icon name={option.icon} />
											</ListItemIcon>
										)}
										{option.label && (
											<ListItemText align={option.icon ? "left" : "center"}>
												{option.label}
											</ListItemText>
										)}
									</MenuItem>
								) : (
									<Separator key={`separator-${index}`} />
								)
							)}
						</Menu>
					</>
				)}
			</ButtonBase>
		);
	}
);
